import { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { client } from '@/lib/api';

interface Inquiry {
  id: string;
  sentAt: string;
  status: 'pending' | 'received' | 'no_response';
  subject: string;
  body: string;
}

interface InquirySectionProps {
  itemId: string;
  inquiries: Inquiry[];
  vendorEmail: string | null;
  onSendInquiry: () => void;
  onSendFollowUp: () => void;
}

const STALE_DAYS = 10;

function relativeDays(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

function formatSent(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function StatusPill({ status }: { status: Inquiry['status'] }) {
  const styles =
    status === 'received'
      ? 'bg-[var(--color-primary-container)] text-foreground'
      : status === 'no_response'
        ? 'bg-muted text-muted-foreground'
        : 'bg-[var(--color-secondary-container)] text-foreground';
  const label = status === 'received' ? 'Received' : status === 'no_response' ? 'No response' : 'Pending';
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles}`}>
      {label}
    </span>
  );
}

function InquiryCard({
  inquiry,
  itemId,
  isFirst,
  isFollowUp,
  onSendFollowUp,
}: {
  inquiry: Inquiry;
  itemId: string;
  isFirst: boolean;
  isFollowUp: boolean;
  onSendFollowUp: () => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const days = relativeDays(inquiry.sentAt);
  const showFollowUpCta = isFirst && inquiry.status === 'pending' && days >= STALE_DAYS;

  const updateStatus = useMutation({
    mutationFn: async (status: Inquiry['status']) => {
      const res = await client.api['quote-requests'][':id'].$put({
        param: { id: inquiry.id },
        json: { status },
      });
      if (!res.ok) throw new Error('Failed to update inquiry');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    },
    onError: () => toast.error('Failed to update inquiry'),
  });

  const deleteInquiry = useMutation({
    mutationFn: async () => {
      const res = await client.api['quote-requests'][':id'].$delete({
        param: { id: inquiry.id },
      });
      if (!res.ok) throw new Error('Failed to delete inquiry');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      toast.success('Inquiry deleted');
    },
    onError: () => toast.error('Failed to delete inquiry'),
  });

  return (
    <div className="rounded-2xl bg-[var(--color-surface-container-high)] p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          Sent {formatSent(inquiry.sentAt)} · {days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`}
        </div>
        <div className="flex items-center gap-2">
          {isFollowUp && (
            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs">
              Follow-up
            </span>
          )}
          <StatusPill status={inquiry.status} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Inquiry actions"
                className="rounded p-1 text-muted-foreground hover:bg-background/60 hover:text-foreground transition-colors"
              >
                <MoreHorizontal className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {inquiry.status !== 'received' && (
                <DropdownMenuItem onSelect={() => updateStatus.mutate('received')}>
                  Mark as received
                </DropdownMenuItem>
              )}
              {inquiry.status !== 'pending' && (
                <DropdownMenuItem onSelect={() => updateStatus.mutate('pending')}>
                  Mark as pending
                </DropdownMenuItem>
              )}
              {inquiry.status !== 'no_response' && (
                <DropdownMenuItem onSelect={() => updateStatus.mutate('no_response')}>
                  Mark as no response
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  if (confirm('Delete this inquiry record? The email already sent cannot be unsent.')) {
                    deleteInquiry.mutate();
                  }
                }}
                className="text-destructive focus:text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {inquiry.subject && (
        <div className="text-sm font-medium">{inquiry.subject}</div>
      )}
      <details className="group">
        <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors">
          ▸ View message
        </summary>
        <pre className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground bg-background/50 rounded-lg p-3 font-sans">
          {inquiry.body || '(no message recorded)'}
        </pre>
      </details>
      {showFollowUpCta && (
        <div className="flex justify-end pt-1">
          <Button size="sm" onClick={onSendFollowUp}>
            <Send className="mr-1.5 h-4 w-4" />
            Send follow-up
          </Button>
        </div>
      )}
    </div>
  );
}

export default function InquirySection({
  itemId,
  inquiries,
  vendorEmail,
  onSendInquiry,
  onSendFollowUp,
}: InquirySectionProps) {
  if (inquiries.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-6 text-center space-y-3">
        <p className="text-sm text-muted-foreground">No inquiry sent yet.</p>
        <Button
          size="sm"
          onClick={onSendInquiry}
          disabled={!vendorEmail}
          title={!vendorEmail ? 'Add a vendor email first' : undefined}
        >
          <Send className="mr-1.5 h-4 w-4" />
          Send Inquiry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {inquiries.map((inquiry, idx) => (
        <InquiryCard
          key={inquiry.id}
          inquiry={inquiry}
          itemId={itemId}
          isFirst={idx === 0}
          isFollowUp={idx < inquiries.length - 1}
          onSendFollowUp={onSendFollowUp}
        />
      ))}
    </div>
  );
}
